import React, { forwardRef } from "react"
import cx from "classnames"

import {
  ButtonTemplatePropsType,
  getButtonCommonClassNames,
  getButtonContent,
} from "../helpers"
import Button, { ButtonProps, LinkProps } from "../ButtonComponent"

type SmallCommonPropsType = ButtonTemplatePropsType<{
  variant?:
    | "primary"
    | "warning"
    | "secondary"
    | "tertiary"
    | "business"
    | "success"
    | "neutral"
  destructive?: boolean
}>

export type SmallButtonPropsType = SmallCommonPropsType & ButtonProps
export type SmallLinkPropsType = SmallCommonPropsType & LinkProps

export type SmallButtonElement = React.ReactElement<SmallButtonPropsType>

const getComputedClassName = ({
  className,
  variant,
  destructive,
}: SmallCommonPropsType) =>
  cx(className, "cob-Button__small", {
    "cob-Button__small--primary": variant === "primary",
    "cob-Button__small--destructive": destructive,
    "cob-Button__small--warning": variant === "warning",
    "cob-Button__small--secondary": variant === "secondary",
    "cob-Button__small--tertiary": variant === "tertiary",
    "cob-Button__small--business": variant === "business",
    "cob-Button__small--success": variant === "success",
    "cob-Button__small--neutral": variant === "neutral",
  })

const _SmallButton = forwardRef<HTMLButtonElement, SmallButtonPropsType>(
  (props: SmallButtonPropsType, ref) => {
    const {
      children,
      variant,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restButtonProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })
    return (
      <Button
        {...restButtonProps}
        className={getComputedClassName({
          className: buttonClassNames,
          variant,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button>
    )
  }
)
_SmallButton.displayName = "SmallButton"

const SmallLink = forwardRef<HTMLAnchorElement, SmallLinkPropsType>(
  (props: SmallLinkPropsType, ref) => {
    const {
      children,
      variant,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restLinkProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })
    return (
      <Button.Link
        {...restLinkProps}
        className={getComputedClassName({
          className: buttonClassNames,
          variant,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button.Link>
    )
  }
)
SmallLink.displayName = "SmallLink"

const SmallButton = Object.assign(_SmallButton, { Link: SmallLink })

export default SmallButton
